const API_BASE = "/api";

/** Active album id sent as X-Album on every API call. */
let activeAlbum: string | null = null;

export function setApiAlbum(id: string | null) {
  activeAlbum = id && id.trim() ? id.trim() : null;
}

export function getApiAlbum(): string | null {
  return activeAlbum;
}

function withAlbumHeaders(init?: HeadersInit): Headers {
  const h = new Headers(init);
  if (activeAlbum) {
    h.set("X-Album", activeAlbum);
  }
  return h;
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg = body.error || `Request failed: ${res.status}`;
    throw new Error(msg);
  }
  return res.json();
}

export async function apiGetQuiet<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "same-origin",
    headers: withAlbumHeaders(),
  });
  return handleResponse(res);
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "same-origin",
    headers: withAlbumHeaders(),
  });
  return handleResponse(res);
}

/** GET that supports conditional ?ver= responses. Returns null on 304. */
export async function apiGetMaybe<T>(path: string): Promise<T | null> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "same-origin",
    headers: withAlbumHeaders(),
  });
  if (res.status === 304) return null;
  return handleResponse(res);
}

export async function apiPut<T>(path: string, data: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "PUT",
    credentials: "same-origin",
    headers: withAlbumHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function apiPost<T>(path: string, data: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    credentials: "same-origin",
    headers: withAlbumHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function apiDelete<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "DELETE",
    credentials: "same-origin",
    headers: withAlbumHeaders(),
  });
  return handleResponse(res);
}

export async function apiPostQuiet<T>(path: string, data: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    credentials: "same-origin",
    headers: withAlbumHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

/** Subscribe to an SSE stream endpoint. Calls onEvent for each named event.
 *  Returns an abort function to cancel the stream. */
export function apiSSE(
  path: string,
  onEvent: (event: string, data: unknown) => void,
  onError?: (err: Error) => void,
): () => void {
  const ctrl = new AbortController();
  (async () => {
    try {
      const res = await fetch(`${API_BASE}${path}`, {
        signal: ctrl.signal,
        credentials: "same-origin",
        headers: withAlbumHeaders(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Request failed: ${res.status}`);
      }
      const reader = res.body!.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let currentEvent = "message";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop() || "";
        for (const line of lines) {
          if (line.startsWith("event:")) {
            currentEvent = line.slice(6).trim();
          } else if (line.startsWith("data:")) {
            const raw = line.slice(5).trim();
            try {
              onEvent(currentEvent, JSON.parse(raw));
            } catch {
              onEvent(currentEvent, raw);
            }
            currentEvent = "message";
          }
        }
      }
    } catch (e) {
      if ((e as Error).name !== "AbortError") {
        onError?.(e instanceof Error ? e : new Error(String(e)));
      }
    }
  })();
  return () => ctrl.abort();
}

type ToastFn = (type: "success" | "error", msg: string) => void;

/** Wraps a try/ok-check/catch pattern: calls `fn`, toasts success or error. Returns true on success. */
export async function apiAction(
  fn: () => Promise<{ ok: boolean; error?: string }>,
  successMsg: string,
  showToast: ToastFn,
): Promise<boolean> {
  try {
    const res = await fn();
    if (res.ok) {
      showToast("success", successMsg);
      return true;
    }
    showToast("error", res.error ?? "Failed");
    return false;
  } catch (e) {
    showToast("error", e instanceof Error ? e.message : "Failed");
    return false;
  }
}

/** POST multipart FormData (do not set Content-Type — browser sets boundary). */
export async function apiUploadForm<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    credentials: "same-origin",
    headers: withAlbumHeaders(),
    body: form,
  });
  return handleResponse(res);
}
